import { api } from './apiClient';

/**
 * Cost & Profitability Control Center API.
 *
 * All actual / revenue / margin figures are computed server-side from REAL
 * POSTED journals (never mocked); cost-center budgets come from stored
 * budget_lines. The FE apiClient auto-unwraps the { status, data } envelope,
 * so these resolve to the inner payloads directly.
 */

export interface CostProfitSummary {
  financialYear: string;
  totalCostCenters: number;
  activeCostCenters: number;
  profitCenters: number;
  totalBudget: number;
  actualCost: number;
  revenue: number;
  operatingProfit: number;
  operatingMargin: number | null;
}

export interface CostCenterRow {
  id: number | null;
  code: string;
  name: string;
  type: string;
  parentId: number | null;
  departmentId: number | null;
  departmentName: string | null;
  managerName: string | null;
  plant: string | null;
  status: string;
  isActive: boolean;
  budget: number;
  actual: number;
  variance: number;
  variancePct: number | null;
  utilisation: number | null;
  forecast: number;
  favourable: boolean;
}

export interface CostCentersAnalytics {
  financialYear: string;
  elapsedFraction: number;
  rows: CostCenterRow[];
  totals: { budget: number; actual: number; forecast: number; variance: number };
}

export interface ProfitCenterRow {
  id: number;
  code: string;
  name: string;
  type: string;
  segment: string | null;
  region: string | null;
  managerName: string | null;
  plant: string | null;
  parentId: number | null;
  status: string;
  isActive: boolean;
  revenue: number;
  cogs: number;
  grossProfit: number;
  opex: number;
  operatingProfit: number;
  margin: number | null;
  grossMargin: number | null;
  targetMargin: number | null;
  marginVsTarget: number | null;
  rank: number;
}

export interface ProfitCentersAnalytics {
  financialYear: string;
  rows: ProfitCenterRow[];
  totals: { revenue: number; cogs: number; grossProfit: number; opex: number; operatingProfit: number; margin: number | null };
}

export interface JournalLine {
  id: number;
  journalEntryId: number;
  entryNumber: string;
  entryDate: string;
  description: string;
  reference: string | null;
  accountId: number;
  accountCode: string;
  accountName: string;
  accountType?: string;
  debit: number;
  credit: number;
  amount?: number;
  narration: string | null;
}

export interface CostCenter360 {
  financialYear: string;
  header: {
    id: number; code: string; name: string; description: string | null; type: string;
    parentId: number | null; parentName: string | null;
    departmentId: number | null; departmentName: string | null;
    managerName: string | null; plant: string | null; status: string; isActive: boolean;
    effectiveFrom: string | null; effectiveTo: string | null;
    budget: number; actual: number; variance: number; utilisation: number | null;
    forecast: number; elapsedFraction: number;
  };
  monthly: { month: string; actual: number }[];
  expenseByAccount: { accountId: number; accountCode: string; accountName: string; budget: number; actual: number; variance: number; utilisation: number | null }[];
  lines: JournalLine[];
}

export interface ProfitCenter360 {
  financialYear: string;
  header: {
    id: number; code: string; name: string; description: string | null; type: string;
    segment: string | null; region: string | null; managerName: string | null; plant: string | null;
    parentId: number | null; status: string; isActive: boolean;
    effectiveFrom: string | null; effectiveTo: string | null;
  };
  pnl: {
    revenue: number; cogs: number; grossProfit: number; opex: number; operatingProfit: number;
    margin: number | null; targetMargin: number | null; grossMargin: number | null; marginVsTarget: number | null;
  };
  monthly: { month: string; revenue: number; cost: number; profit: number; margin: number | null }[];
  lines: JournalLine[];
}

export interface CostCenterInput {
  code?: string; name?: string; description?: string; costCenterType?: string;
  parentId?: number | null; departmentId?: number | null; plant?: string; managerName?: string;
  status?: string; isActive?: boolean;
}
export interface ProfitCenterInput {
  code?: string; name?: string; description?: string; pcType?: string; segment?: string;
  region?: string; plant?: string; managerName?: string; targetMargin?: number | null | string;
  status?: string; isActive?: boolean;
}

export const costProfitService = {
  summary: (): Promise<CostProfitSummary> => api.get('/cost-profit-summary'),
  costCentersAnalytics: (): Promise<CostCentersAnalytics> => api.get('/cost-centers/analytics'),
  profitCentersAnalytics: (): Promise<ProfitCentersAnalytics> => api.get('/profit-centers/analytics'),
  costCenter360: (id: number): Promise<CostCenter360> => api.get(`/cost-centers/${id}/360`),
  profitCenter360: (id: number): Promise<ProfitCenter360> => api.get(`/profit-centers/${id}/360`),

  // Masters CRUD (list is used to feed parent-selects in the forms)
  listCostCenters: (): Promise<{ costCenters: any[] }> => api.get('/cost-centers'),
  createCostCenter: (d: CostCenterInput) => api.post('/cost-centers', d),
  updateCostCenter: (id: number, d: CostCenterInput) => api.put(`/cost-centers/${id}`, d),
  listProfitCenters: (): Promise<{ profitCenters: any[] }> => api.get('/profit-centers'),
  createProfitCenter: (d: ProfitCenterInput) => api.post('/profit-centers', d),
  updateProfitCenter: (id: number, d: ProfitCenterInput) => api.put(`/profit-centers/${id}`, d),
};

export default costProfitService;
